/**
 * AdminCommandPalette — ⌘K / Ctrl+K quick navigation across the whole admin.
 *
 * 2026 UX: a command palette is the fastest path between any two admin pages
 * without hunting the sidebar. Flattens ADMIN_SIDEBAR_NAV_GROUPS (sections,
 * items and their sub-items) into a single fuzzy-searchable list and routes
 * via react-router on select. Also exposes the "Ver sitio público" action.
 *
 * Controlled by the parent (AdminLayout) so a header button can open it too.
 */

import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  ADMIN_SIDEBAR_NAV_GROUPS,
  type AdminSidebarNavGroup,
} from "@/components/admin/adminSidebarNav";
import { normalizeLanguageCode } from "@/lib/languages";

export interface AdminCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Filtered nav (e.g. hide super-admin-only routes). Defaults to full sidebar. */
  navGroups?: readonly AdminSidebarNavGroup[];
}

export function AdminCommandPalette({
  open,
  onOpenChange,
  navGroups = ADMIN_SIDEBAR_NAV_GROUPS,
}: AdminCommandPaletteProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLanguageCode(i18n.resolvedLanguage ?? i18n.language ?? "");

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      {/* Excellence: dense, high-contrast command surface inside dark admin shell */}
      <div className="border-b border-white/[0.06] bg-black/40">
        <CommandInput placeholder={t("adminShell.commandSearchPlaceholder")} />
      </div>
      <CommandList className="max-h-[min(420px,70vh)]">
        <CommandEmpty>{t("adminShell.commandEmpty")}</CommandEmpty>

        {navGroups.map((group) => (
          <CommandGroup key={group.label} heading={group.label}>
            {group.items.map((item) => (
              <CommandItem
                key={item.to}
                /* value drives fuzzy search — include label + hint */
                value={`${item.label} ${item.hint ?? ""}`}
                onSelect={() => go(item.to)}
              >
                <item.icon className="mr-2 h-4 w-4 opacity-80" aria-hidden />
                <span className="flex-1">{item.label}</span>
                {item.badge != null && item.badge !== "" && (
                  <span className="ml-2 rounded bg-amber-400/90 px-1 py-0 text-[10px] font-medium text-black">
                    {item.badge}
                  </span>
                )}
                {item.hint && (
                  <span className="ml-3 truncate text-xs text-muted-foreground">
                    {item.hint}
                  </span>
                )}
              </CommandItem>
            ))}

            {group.items
              .filter((i) => i.children?.length)
              .flatMap((parent) =>
                parent.children!.map((child) => (
                  <CommandItem
                    key={child.to}
                    value={`${parent.label} ${child.label}`}
                    onSelect={() => go(child.to)}
                  >
                    <child.icon className="mr-2 h-4 w-4 opacity-60" aria-hidden />
                    <span className="flex-1">{child.label}</span>
                    <span className="ml-3 truncate text-xs text-muted-foreground">
                      {parent.label}
                    </span>
                  </CommandItem>
                )),
              )}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading={t("adminShell.commandActions")}>
          <CommandItem
            value={t("adminShell.viewSitePublic")}
            onSelect={() => {
              onOpenChange(false);
              window.open(`/?lang=${currentLang}`, "_blank", "noopener,noreferrer");
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4 opacity-80" aria-hidden />
            {t("adminShell.viewSitePublic")}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
